import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import styled from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { ActiveUserAvatar } from '@sorare/core/src/components/user/ActiveUserAvatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { UserName } from '@sorare/core/src/components/user/UserName';

import type {
  OwnerMyClubLink_User,
  TokenOwner_anyCard,
} from './__generated__/index.graphql.ts';

type Props = {
  card: TokenOwner_anyCard;
  withAvatar?: boolean;
  variant?: ComponentProps<typeof OwnerMyClubLink>['variant'];
};

// min-width: 0 allow child to have text-overflow: ellipsis;
const StyledMyClubLink = styled(MyClubLink)`
  max-width: 100%;
  min-width: 0;
`;

const DisabledMyClubLink = styled(Horizontal)`
  opacity: 0.5;
`;

const StyledEllipsis = styled(Ellipsis)`
  font: var(--t-bold) var(--t-12);
  &.medium {
    font: var(--t-bold) var(--t-14);
  }
`;

type GalleryProps = {
  sport: Sport;
  user: OwnerMyClubLink_User;
  withAvatar: boolean;
  onClick?: () => void;
  variant?: 'small' | 'medium';
};

export const OwnerMyClubLink = ({
  sport,
  user,
  withAvatar,
  onClick,
  variant = 'small',
}: GalleryProps) => {
  return (
    <StyledMyClubLink
      user={user}
      sport={sport}
      WhenSuspended={DisabledMyClubLink}
      onClick={onClick}
    >
      <Horizontal>
        {!!withAvatar && <ActiveUserAvatar user={user} size={2} />}
        <StyledEllipsis className={variant}>
          <UserName user={user} />
        </StyledEllipsis>
      </Horizontal>
    </StyledMyClubLink>
  );
};

export const TokenOwner = ({ card, withAvatar = false, variant }: Props) => {
  if (card?.user) {
    return (
      <OwnerMyClubLink
        user={card.user}
        sport={card.sport}
        withAvatar={withAvatar}
        variant={variant}
      />
    );
  }
  return null;
};

OwnerMyClubLink.fragments = {
  user: gql`
    fragment OwnerMyClubLink_User on User {
      slug
      ...ActiveUserAvatar_user
      ...UserName_publicUserInfoInterface
    }
    ${ActiveUserAvatar.fragments.user}
    ${UserName.fragments.user}
  ` as TypedDocumentNode<OwnerMyClubLink_User>,
};

TokenOwner.fragments = {
  anyCard: gql`
    fragment TokenOwner_anyCard on AnyCardInterface {
      slug
      sport
      user {
        slug
        ...OwnerMyClubLink_User
      }
    }
    ${OwnerMyClubLink.fragments.user}
  ` as TypedDocumentNode<TokenOwner_anyCard>,
};
